import {
  getGetFrontRichiesteIdAllegatiQueryKey,
  getGetFrontRichiesteIdGestoreQueryKey,
  getGetFrontRichiesteIdProcuratoreQueryKey,
  usePostFrontRichiesteIdGestore,
  usePostFrontRichiesteIdProcuratore,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getGetFrontRichiesteIdRiepilogoDownloadQueryKey } from '@framework/api-client/api/inquiries-compile/riepilogo-richiesta/riepilogo-richiesta.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { modalConfirm } from '@framework/engine/ui/modal/modalConfirm.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '../../../../core/network/queryClient.ts';
import { type ChangeSubmissionManagerValues } from '../components/ChangeSubmissionManagerButton.tsx';

/**
 * Hook che gestisce il cambio di Procuratore/Gestore chiamando l'API dedicata
 */
export function useChangeSubmissionManager(type: 'manager' | 'attorney') {
  const { t } = useTranslation();
  const updateManager = usePostFrontRichiesteIdGestore();
  const updateAttorney = usePostFrontRichiesteIdProcuratore({
    request: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  const currentUser = useCurrentUser();

  const update = useMemo(
    () => ({
      mutateAsync: async (
        submissionId: string,
        values: ChangeSubmissionManagerValues,
      ) => {
        if (type === 'manager') {
          // Se l'utente sta rimuovendo se stesso come gestore
          // chiediamo una conferma
          if (currentUser?.utenteLoggato?.codiceFiscale !== values.fiscalCode) {
            const isConfirmed = await modalConfirm({
              width: 500,
              title: t(
                'change-submission-manager.conferma-cambio-gestore',
                `Conferma cambio gestore`,
              ),
              // biome-ignore format: <tbd>
              content: t("change-submission-manager.sicuro-di-voler-sostituire-il-gestore", `Sei sicuro di voler sostituire il gestore della richiesta? Una volta completata l'operazione potresti perdere la possibilità di accedere alla richiesta.`),
            });

            if (!isConfirmed) return false;
          }

          await updateManager.mutateAsync({
            id: submissionId,
            data: {
              codiceFiscale: values.fiscalCode,
            },
          });
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdGestoreQueryKey(submissionId),
          );
        } else {
          await updateAttorney.mutateAsync({
            id: submissionId,
            data: {
              codiceFiscale: values.fiscalCode,
              file: values.file as Blob,
              permetteInoltro: values.canSubmit ?? false,
              nome: values.name,
              cognome: values.surname,
              assegnaComeGestore: values.assignAsManager ?? false,
              email: values.email,
            },
          });
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdProcuratoreQueryKey(submissionId),
          );
          // Dobbiamo invalidare anche la cache del gestore, dato che potrebbe cambiare
          // con il flag "assegnaComeGestore"
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdGestoreQueryKey(submissionId),
          );
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdRiepilogoDownloadQueryKey(submissionId),
          );
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdAllegatiQueryKey(submissionId),
          );
        }
        return true;
      },
    }),
    [
      currentUser?.utenteLoggato?.codiceFiscale,
      t,
      type,
      updateAttorney,
      updateManager,
    ],
  );

  return { update };
}
